/**
 * EmptyStateCard Component — Soorgaa Design System
 * 
 * Consistent empty state with icon, clear explanation, and primary recovery action.
 */
import React from "react";
import { Inbox } from "lucide-react";
import { Button } from "./Button";

export interface EmptyStateCardProps {
  title?: string;
  message?: string;
  description?: string;
  icon?: React.ElementType | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyStateCard({
  title = "No items yet",
  message,
  description,
  icon = Inbox,
  action,
  className = "",
}: EmptyStateCardProps) {
  const resolvedMessage = message ?? description ?? "";

  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      return icon;
    }
    if (typeof icon === "function" || (typeof icon === "object" && icon !== null && !("$$typeof" in icon))) {
      const IconComponent = icon as React.ElementType;
      return <IconComponent size={24} aria-hidden="true" />;
    }
    return <Inbox size={24} aria-hidden="true" />;
  };

  return (
    <div
      className={`text-center py-12 sm:py-16 bg-card rounded-lg border border-dashed border-border p-6 sm:p-8 ${className}`.trim()}
    >
      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto mb-3 text-muted-foreground/70">
        {renderIcon()}
      </div>
      {title && <h3 className="font-bold text-base sm:text-lg text-foreground">{title}</h3>}
      {resolvedMessage && (
        <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mt-1 leading-relaxed">
          {resolvedMessage}
        </p>
      )}
      {action && (
        <div className="mt-4">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
}
